// Browser microphone recording for narration capture.
// Uses MediaRecorder with WebM/Opus (universal in evergreen browsers), exposes
// a live level meter via AnalyserNode RMS, returns a downloadable Blob on stop.
//
// Phase A: download-to-disk only. Phase B will add R2 upload via signed URL.

export interface RecorderEvents {
  onLevel?: (rms: number) => void;        // 0..1
  onTimeUpdate?: (elapsedMs: number) => void;
  onError?: (err: Error) => void;
}

export interface RecorderResult {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  sampleRate: number;
}

export class NarrationRecorder {
  private stream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private chunks: Blob[] = [];
  private startedAt: number = 0;
  private events: RecorderEvents;
  private rafId: number | null = null;

  constructor(events: RecorderEvents = {}) {
    this.events = events;
  }

  async start(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000,
        },
      });
    } catch (err) {
      this.events.onError?.(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }

    // Level meter via Web Audio analyser tap
    this.audioCtx = new AudioContext();
    const source = this.audioCtx.createMediaStreamSource(this.stream);
    this.analyser = this.audioCtx.createAnalyser();
    this.analyser.fftSize = 1024;
    source.connect(this.analyser);
    this.runLevelLoop();

    // MediaRecorder — pick best available mime
    const mime = pickMimeType();
    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType: mime,
      audioBitsPerSecond: 64_000,
    });
    this.chunks = [];
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.mediaRecorder.onerror = (e) => {
      this.events.onError?.(new Error('MediaRecorder error: ' + String(e)));
    };
    this.startedAt = performance.now();
    this.mediaRecorder.start(250); // 250ms chunks → smooth progress

    const tick = () => {
      this.events.onTimeUpdate?.(performance.now() - this.startedAt);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  async stop(): Promise<RecorderResult | null> {
    if (!this.mediaRecorder) return null;
    if (this.rafId != null) cancelAnimationFrame(this.rafId);

    const recorder = this.mediaRecorder;
    const stopped = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });
    recorder.stop();
    await stopped;

    const durationMs = Math.round(performance.now() - this.startedAt);
    const mimeType = recorder.mimeType || 'audio/webm';
    const blob = new Blob(this.chunks, { type: mimeType });
    const sampleRate = this.audioCtx?.sampleRate ?? 24000;

    this.cleanup();
    return { blob, mimeType, durationMs, sampleRate };
  }

  cancel() {
    this.cleanup();
  }

  private cleanup() {
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.mediaRecorder = null;
    this.chunks = [];
    if (this.stream) {
      for (const t of this.stream.getTracks()) t.stop();
      this.stream = null;
    }
    if (this.audioCtx) {
      void this.audioCtx.close();
      this.audioCtx = null;
    }
    this.analyser = null;
  }

  private runLevelLoop() {
    if (!this.analyser) return;
    const buf = new Float32Array(this.analyser.fftSize);
    const loop = () => {
      if (!this.analyser) return;
      this.analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      this.events.onLevel?.(Math.min(1, rms * 3));
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }
}

function pickMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* ignore */
    }
  }
  return '';
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
